import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, Crown, Download, Infinity as InfinityIcon, RefreshCw, Search, ShoppingCart, Wrench } from 'lucide-react';
import {
  apiService, type ActiveGradeRowDTO, type ActiveGradesResponse, type GradeRepairPreview, type GradeRepairResult,
  type GradeRepairRunDTO,
} from '@/services/api.service';
import { useAuthStore } from '@/stores/auth.store';
import { formatPlayerName } from '@/utils/playerName';
import {
  aberrantYear, effectiveEnd, filterRows, formatEnd, gradeLabel, remainingLabel, repairActionLabel, repairStatusLabel,
  rowKind, sortRows, type SortOrder,
} from './activeGrades';
import { TebexImportPanel } from './TebexImportPanel';
import styles from './ActiveGradesSection.module.scss';

/** Lignes affichées d'un coup ; « Afficher plus » en ajoute autant. */
const PAGE = 200;

/** Fichier JSON proposé au téléchargement (aperçu relu, journal d'une correction). */
function download(name: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function stamp(): string {
  return new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
}

function PlayerCell({ row }: { row: { uuid: string; name?: string; displayName?: string; online: boolean; serverName?: string } }) {
  return (
    <div className={styles.player}>
      <span className={row.online ? styles.dotOnline : styles.dotOffline} title={row.online ? 'En ligne' : 'Hors ligne'} />
      <div className={styles.playerText}>
        <span className={styles.playerName}>{formatPlayerName(row.name, row.displayName, row.uuid.slice(0, 13) + '…')}</span>
        {row.online && row.serverName && <span className={styles.playerSub}>sur {row.serverName}</span>}
      </div>
    </div>
  );
}

/**
 * Section « Grades actifs » de l'espace Statistiques : joueurs qui portent un grade vendu, avec la
 * fin de chaque grade et le temps restant, lus dans LuckPerms par le satellite du projet. Lecture
 * seule ; la correction des fins aberrantes (admins du panel) s'ouvre depuis l'alerte.
 */
export function ActiveGradesSection() {
  const isAdmin = useAuthStore((s) => !!s.user?.isAdmin);
  const [data, setData] = useState<ActiveGradesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [grade, setGrade] = useState('all');
  const [order, setOrder] = useState<SortOrder>('remaining-asc');
  const [shown, setShown] = useState(PAGE);
  const [shownLife, setShownLife] = useState(PAGE);
  const [repairOpen, setRepairOpen] = useState(false);
  const [tebexOpen, setTebexOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await apiService.getActiveGrades();
    if (res.status === 404) {
      setError("Le satellite de ce projet ne sert pas encore cette section : il faut publier sa nouvelle version.");
    } else if (res.error) {
      setError(res.error);
    } else if (res.data) {
      setData(res.data);
    }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const now = data?.now ?? Math.floor(Date.now() / 1000);
  const filtered = useMemo(() => filterRows(data?.players ?? [], query, grade), [data, query, grade]);
  const timed = useMemo(() => sortRows(filtered.filter((r) => r.expiry !== undefined), order), [filtered, order]);
  const forLife = useMemo(() => sortRows(filtered.filter((r) => r.expiry === undefined), 'name'), [filtered]);
  const aberrant = useMemo(() => (data?.players ?? []).filter((r) => r.aberrant), [data]);
  const aberrantPassed = aberrant.filter((r) => rowKind(r, now) === 'aberrant_passe').length;
  const totals = useMemo(() => {
    const counts = data?.counts ?? [];
    return {
      players: new Set((data?.players ?? []).map((r) => r.uuid)).size,
      temporary: counts.reduce((s, c) => s + c.temporary, 0),
      permanent: counts.reduce((s, c) => s + c.permanent, 0),
    };
  }, [data]);

  if (loading && !data) {
    return (
      <div className={styles.state}>
        <div className={styles.spinner} />
        <p>Lecture des grades dans LuckPerms...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className={styles.state}>
        <AlertCircle size={32} />
        <h3>Erreur de chargement</h3>
        <p>{error}</p>
        <button className={styles.button} onClick={load}>Réessayer</button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className={styles.section}>
      <div className={styles.counts}>
        <div className={`${styles.countCard} ${styles.countTotal}`}>
          <span className={styles.countValue}>{totals.players}</span>
          <span className={styles.countLabel}>joueurs gradés</span>
          <span className={styles.countSub}>{totals.temporary} à durée, {totals.permanent} à vie</span>
        </div>
        {data.counts.map((c) => (
          <button
            key={c.grade}
            type="button"
            className={`${styles.countCard} ${grade === c.grade ? styles.countActive : ''}`}
            onClick={() => { setGrade(grade === c.grade ? 'all' : c.grade); setShown(PAGE); }}
            title={grade === c.grade ? 'Montrer tous les grades' : `Ne montrer que ${gradeLabel(c.grade)}`}
          >
            <span className={styles.countValue}>{c.players}</span>
            <span className={styles.countLabel}>{gradeLabel(c.grade)}</span>
            <span className={styles.countSub}>
              {c.permanent > 0 ? `dont ${c.permanent} à vie` : c.players > 0 ? 'tous à durée' : 'aucun joueur'}
            </span>
            {c.aberrant > 0 && <span className={styles.countBadge} title="Dates de fin aberrantes">{c.aberrant} date{c.aberrant > 1 ? 's' : ''} fausse{c.aberrant > 1 ? 's' : ''}</span>}
          </button>
        ))}
      </div>

      {data.failures.length > 0 && (
        <div className={styles.notice}>
          <AlertCircle size={16} />
          <span>LuckPerms n'a pas répondu pour : {data.failures.map(gradeLabel).join(', ')}. Ces grades manquent ci-dessous.</span>
        </div>
      )}

      {aberrant.length > 0 && (
        <div className={styles.alert}>
          <AlertTriangle size={20} className={styles.alertIcon} />
          <div className={styles.alertText}>
            <strong>
              {aberrant.length} grade{aberrant.length > 1 ? 's ont' : ' a'} une date de fin fausse, après l'an 3000.
            </strong>
            <span>
              La boîte aux lettres (MailBoxMod) l'a écrite en millisecondes là où LuckPerms attend des secondes :
              pour le jeu, ces grades ne finissent jamais. La vraie date (valeur divisée par 1000) est montrée à côté.
              {aberrantPassed > 0 && ` Pour ${aberrantPassed} d'entre eux, la vraie date est déjà passée : le joueur garde un grade qu'il devrait avoir perdu.`}
            </span>
          </div>
          {isAdmin && (
            <button className={styles.buttonWarning} onClick={() => setRepairOpen(true)}>
              <Wrench size={16} /> Corriger les dates...
            </button>
          )}
        </div>
      )}

      {repairOpen && isAdmin && <GradeRepairPanel onClose={() => setRepairOpen(false)} onDone={load} />}
      {tebexOpen && isAdmin && <TebexImportPanel onClose={() => setTebexOpen(false)} onDone={load} />}

      <div className={styles.toolbar}>
        <label className={styles.search}>
          <Search size={16} />
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShown(PAGE); setShownLife(PAGE); }}
            placeholder="Rechercher un pseudo, un gamertag ou un uuid"
          />
        </label>
        <select className={styles.select} value={grade} onChange={(e) => { setGrade(e.target.value); setShown(PAGE); }}>
          <option value="all">Tous les grades</option>
          {data.grades.map((g) => <option key={g} value={g}>{gradeLabel(g)}</option>)}
        </select>
        <select className={styles.select} value={order} onChange={(e) => setOrder(e.target.value as SortOrder)}>
          <option value="remaining-asc">Temps restant : bientôt finis d'abord</option>
          <option value="remaining-desc">Temps restant : les plus longs d'abord</option>
          <option value="name">Pseudo</option>
        </select>
        <button className={styles.buttonGhost} onClick={load} disabled={loading} title="Relire LuckPerms">
          <RefreshCw size={16} className={loading ? styles.spin : undefined} /> Actualiser
        </button>
        {isAdmin && !tebexOpen && (
          <button className={styles.buttonGhost} onClick={() => setTebexOpen(true)} title="Vérifier que chaque achat Tebex encore en cours a son grade">
            <ShoppingCart size={16} /> Comparer à l'export Tebex...
          </button>
        )}
      </div>

      {data.namesPartial && (
        <p className={styles.hint}>Certains joueurs n'ont pas pu être nommés à temps : leur uuid est affiché. Actualise pour réessayer.</p>
      )}

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <h3><Crown size={18} /> Grades à durée</h3>
          <span className={styles.cardCount}>{timed.length} ligne{timed.length > 1 ? 's' : ''}</span>
        </div>
        {timed.length === 0 ? (
          <p className={styles.empty}>Aucun grade à durée{query || grade !== 'all' ? ' pour cette recherche' : ''}.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>Joueur</th><th>Grade</th><th>Temps restant</th><th>Date de fin</th></tr>
              </thead>
              <tbody>
                {timed.slice(0, shown).map((r) => <TimedRow key={`${r.uuid}-${r.grade}`} row={r} now={now} />)}
              </tbody>
            </table>
          </div>
        )}
        {timed.length > shown && (
          <button className={styles.more} onClick={() => setShown(shown + PAGE)}>
            Afficher plus ({timed.length - shown} de plus)
          </button>
        )}
      </div>

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <h3><InfinityIcon size={18} /> Grades à vie</h3>
          <span className={styles.cardCount}>{forLife.length} ligne{forLife.length > 1 ? 's' : ''}</span>
        </div>
        <p className={styles.cardNote}>
          Sans date de fin dans LuckPerms. Parmi eux, ceux envoyés par la boutique avec le champ mal écrit
          « experity » sont arrivés sans durée : la durée voulue est perdue et on ne peut pas les distinguer d'un
          grade donné à vie. Ils sont seulement montrés ici, la correction n'y touche pas.
        </p>
        {forLife.length === 0 ? (
          <p className={styles.empty}>Aucun grade à vie{query || grade !== 'all' ? ' pour cette recherche' : ''}.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th>Joueur</th><th>Grade</th><th>Fin</th></tr></thead>
              <tbody>
                {forLife.slice(0, shownLife).map((r) => (
                  <tr key={`${r.uuid}-${r.grade}`}>
                    <td><PlayerCell row={r} /></td>
                    <td><GradeTag row={r} /></td>
                    <td className={styles.muted}>à vie</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {forLife.length > shownLife && (
          <button className={styles.more} onClick={() => setShownLife(shownLife + PAGE)}>
            Afficher plus ({forLife.length - shownLife} de plus)
          </button>
        )}
      </div>

      <p className={styles.hint}>
        Relevé du {formatEnd(data.now)}, lu dans LuckPerms. Un grade donné à un groupe (héritage) n'est pas compté :
        seuls les grades portés directement par le joueur le sont.
      </p>
    </div>
  );
}

function GradeTag({ row }: { row: ActiveGradeRowDTO }) {
  return (
    <span className={styles.gradeCell}>
      <span className={styles.gradeTag}>{gradeLabel(row.grade)}</span>
      {row.context && row.context.length > 0 && <span className={styles.context}>{row.context.join(', ')}</span>}
    </span>
  );
}

function TimedRow({ row, now }: { row: ActiveGradeRowDTO; now: number }) {
  const kind = rowKind(row, now);
  const end = effectiveEnd(row);
  const remaining = remainingLabel(row, now);
  return (
    <tr className={row.aberrant ? styles.rowAberrant : undefined}>
      <td><PlayerCell row={row} /></td>
      <td><GradeTag row={row} /></td>
      <td>
        <span className={
          kind === 'aberrant_passe' ? styles.remainingPassed
            : end && end - now < 3 * 86_400 ? styles.remainingSoon : styles.remaining
        }>
          {remaining}
        </span>
        {kind === 'aberrant_passe' && <span className={styles.remainingNote}>toujours actif en jeu</span>}
      </td>
      <td>
        {row.aberrant ? (
          <div className={styles.endAberrant}>
            <span className={styles.wrongDate} title={`Valeur écrite : ${row.expiry}`}>
              <AlertTriangle size={13} /> {aberrantYear(row.expiry!)} (date fausse)
            </span>
            <span className={styles.realDate}>
              {end ? <>vraie date : <strong>{formatEnd(end)}</strong></> : 'vraie date illisible'}
            </span>
          </div>
        ) : (
          formatEnd(row.expiry!)
        )}
      </td>
    </tr>
  );
}

// ==================== Correction des dates fausses (admins) ====================

const CONFIRM_WORD = 'CORRIGER';

/**
 * Correction des fins de grade écrites en millisecondes : aperçu complet d'abord (rien n'est écrit),
 * puis correction seulement après confirmation explicite (case cochée et mot tapé). Le satellite
 * refait la liste au moment de corriger et refuse si elle a changé depuis l'aperçu.
 */
function GradeRepairPanel({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [preview, setPreview] = useState<GradeRepairPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const [typed, setTyped] = useState('');
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<GradeRepairResult | null>(null);
  const [runs, setRuns] = useState<GradeRepairRunDTO[]>([]);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    setChecked(false);
    setTyped('');
    const [res, journal] = await Promise.all([apiService.getGradeRepairPreview(), apiService.getGradeRepairRuns()]);
    if (res.error) setError(res.error);
    else if (res.data) setPreview(res.data);
    if (journal.data) setRuns(journal.data.runs);
    setLoading(false);
  }, []);

  useEffect(() => { void loadPreview(); }, [loadPreview]);

  const actionable = preview ? preview.toFix + preview.toRemove : 0;
  const canApply = !!preview && preview.journalAvailable && !preview.running && actionable > 0
    && preview.failures.length === 0 && checked && typed.trim() === CONFIRM_WORD && !applying;

  const apply = async () => {
    if (!preview || !canApply) return;
    setApplying(true);
    setError(null);
    const res = await apiService.applyGradeRepair(preview.fingerprint);
    setApplying(false);
    if (res.error) {
      setError(res.error);
      if (res.status === 409) void loadPreview();
      return;
    }
    if (res.data) {
      setResult(res.data);
      const journal = await apiService.getGradeRepairRuns();
      if (journal.data) setRuns(journal.data.runs);
      onDone();
    }
  };

  return (
    <div className={styles.repair}>
      <div className={styles.repairHead}>
        <h3><Wrench size={18} /> Corriger les dates de fin fausses</h3>
        <button className={styles.buttonGhost} onClick={onClose}>Fermer</button>
      </div>

      {loading && <p className={styles.hint}>Lecture de tous les grades dans LuckPerms...</p>}
      {error && <div className={styles.errorBox}><AlertCircle size={16} /> {error}</div>}

      {preview && !result && (
        <>
          <p className={styles.repairIntro}>
            Aperçu complet : rien n'a encore changé. Seuls les grades vendus dont la date de fin dépasse l'an 3000
            sont concernés ({[...preview.scannedGrades, ...preview.failures].map(gradeLabel).join(', ')}).
            <strong> Les grades du staff ne sont jamais touchés</strong>, même avec une date fausse. Les grades à vie
            et les dates normales non plus.
          </p>
          <div className={styles.repairCounts}>
            <span className={styles.pillFix}>{preview.toFix} à corriger : la fin devient la vraie date</span>
            <span className={styles.pillRemove}>{preview.toRemove} à retirer : la vraie date est déjà passée</span>
            {preview.ignored > 0 && <span className={styles.pillIgnore}>{preview.ignored} ignoré{preview.ignored > 1 ? 's' : ''} : vraie date illisible</span>}
          </div>
          {preview.toRemove > 0 && (
            <div className={styles.notice}>
              <AlertTriangle size={16} />
              <span>
                <strong>{preview.toRemove} grade{preview.toRemove > 1 ? 's seront retirés' : ' sera retiré'}</strong> : leur vraie date
                de fin est déjà passée. C'est ce qui aurait dû se passer à cette date sans le défaut.
              </span>
            </div>
          )}
          {!preview.journalAvailable && (
            <div className={styles.errorBox}>
              <AlertCircle size={16} /> Pas de base « satellite » dans la configuration du satellite : sans journal, la correction est refusée.
            </div>
          )}
          {preview.failures.length > 0 && (
            <div className={styles.errorBox}>
              <AlertCircle size={16} /> LuckPerms n'a pas répondu pour : {preview.failures.join(', ')}. Correction impossible tant que l'aperçu n'est pas complet.
            </div>
          )}
          {preview.running && <div className={styles.notice}><AlertCircle size={16} /> Une correction est déjà en cours.</div>}

          {preview.entries.length === 0 ? (
            <p className={styles.empty}>Aucune date de fin fausse : rien à corriger.</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr><th>Joueur</th><th>Grade</th><th>Date fausse (écrite)</th><th>Vraie date (valeur / 1000)</th><th>Ce qui sera fait</th></tr>
                </thead>
                <tbody>
                  {preview.entries.map((e) => (
                    <tr key={`${e.uuid}-${e.grade}-${(e.context ?? []).join(',')}`}>
                      <td><PlayerCell row={e} /></td>
                      <td>
                        <span className={styles.gradeCell}>
                          <span className={styles.gradeTag}>{gradeLabel(e.grade)}</span>
                          {e.context && e.context.length > 0 && <span className={styles.context}>{e.context.join(', ')}</span>}
                        </span>
                      </td>
                      <td>
                        <span className={styles.wrongDate}>{aberrantYear(e.storedExpiry)}</span>
                        <span className={styles.rawValue}>{e.storedExpiry}</span>
                      </td>
                      <td>{e.realExpiry ? formatEnd(e.realExpiry) : 'illisible'}</td>
                      <td className={e.action === 'RETIRER' ? styles.actionRemove : e.action === 'CORRIGER' ? styles.actionFix : styles.muted}>
                        {repairActionLabel(e)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className={styles.hint}>
            Chaque changement est écrit au journal du satellite avec l'ancienne valeur, pour pouvoir revenir en arrière.
            Un joueur connecté garde son ancien grade jusqu'à sa reconnexion.
          </p>

          {actionable > 0 && (
            <div className={styles.confirm}>
              <button className={styles.buttonGhost} onClick={() => download(`apercu-correction-grades-${stamp()}.json`, preview)}>
                <Download size={16} /> Télécharger l'aperçu
              </button>
              <label className={styles.check}>
                <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
                J'ai relu la liste : {preview.toFix} fin{preview.toFix > 1 ? 's' : ''} remplacée{preview.toFix > 1 ? 's' : ''} par la vraie date, {preview.toRemove} grade{preview.toRemove > 1 ? 's' : ''} retiré{preview.toRemove > 1 ? 's' : ''}.
              </label>
              <label className={styles.typed}>
                Tape <code>{CONFIRM_WORD}</code> pour confirmer :
                <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={CONFIRM_WORD} autoComplete="off" />
              </label>
              <button className={styles.buttonDanger} disabled={!canApply} onClick={apply}>
                {applying ? 'Correction en cours...' : `Corriger ${actionable} grade${actionable > 1 ? 's' : ''}`}
              </button>
            </div>
          )}
        </>
      )}

      {result && (
        <>
          <div className={result.failed > 0 ? styles.errorBox : styles.successBox}>
            {result.failed > 0 ? <AlertCircle size={16} /> : <Wrench size={16} />}
            {result.fixed} corrigé{result.fixed > 1 ? 's' : ''}, {result.removed} retiré{result.removed > 1 ? 's' : ''}
            {result.failed > 0 ? `, ${result.failed} en échec (détail ci-dessous)` : ''}. Journal : {result.runId}
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th>Joueur</th><th>Grade</th><th>Ancienne valeur</th><th>Vraie date</th><th>Résultat</th></tr></thead>
              <tbody>
                {result.entries.map((e) => {
                  const st = repairStatusLabel(e.status);
                  return (
                    <tr key={`${e.uuid}-${e.grade}-${(e.context ?? []).join(',')}`}>
                      <td><PlayerCell row={e} /></td>
                      <td><span className={styles.gradeTag}>{gradeLabel(e.grade)}</span></td>
                      <td className={styles.rawValue}>{e.storedExpiry}</td>
                      <td>{e.realExpiry ? formatEnd(e.realExpiry) : 'illisible'}</td>
                      <td className={st.ok ? styles.actionFix : styles.actionRemove}>{st.text}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {runs.length > 0 && (
        <div className={styles.runs}>
          <h4>Journal des corrections</h4>
          {runs.map((r) => (
            <div key={r._id} className={styles.run}>
              <span>
                {formatEnd(r.startedAt)} par {r.by} : {r.kind === 'tebex'
                  ? `pose des achats Tebex, ${r.posed ?? 0} posé(s), ${r.fixed ?? 0} remplacé(s)`
                  : `${r.fixed ?? 0} corrigé(s), ${r.removed ?? 0} retiré(s)`}{r.failed ? `, ${r.failed} en échec` : ''}
              </span>
              <button className={styles.buttonGhost} onClick={() => download(`journal-correction-grades-${r._id}.json`, r)}>
                <Download size={14} /> Journal
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
